import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, addDays, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { createLocalDate } from "@/utils/dateUtils";
import type { ControleProjeto } from "@/types/controle-projeto";

interface RenovarProjetoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projeto: ControleProjeto | null;
  onConfirm: (novaDataInicio: Date, novaDataConclusao: Date) => void;
}

function calcularDuracaoOriginal(projeto: ControleProjeto | null): number | null {
  if (!projeto?.data_inicio || !projeto?.data_conclusao) return null;
  return differenceInDays(createLocalDate(projeto.data_conclusao), createLocalDate(projeto.data_inicio));
}

export function RenovarProjetoDialog({ isOpen, onClose, projeto, onConfirm }: RenovarProjetoDialogProps) {
  const [novaDataInicio, setNovaDataInicio] = useState<Date>();
  const duracaoOriginal = calcularDuracaoOriginal(projeto);
  const novaDataConclusao =
    novaDataInicio && duracaoOriginal !== null ? addDays(novaDataInicio, duracaoOriginal) : undefined;

  const handleConfirm = () => {
    if (novaDataInicio && novaDataConclusao) {
      onConfirm(novaDataInicio, novaDataConclusao);
      onClose();
      setNovaDataInicio(undefined);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Renovar Projeto</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            <p>Projeto: #{projeto?.id?.slice(0, 8)}</p>
            <p>Duração original: {duracaoOriginal ?? 0} dias</p>
          </div>

          <div className="space-y-2">
            <Label>Nova Data de Início</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !novaDataInicio && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {novaDataInicio ? (
                    format(novaDataInicio, "dd/MM/yyyy", { locale: ptBR })
                  ) : (
                    "Selecione a nova data de início"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={novaDataInicio}
                  onSelect={setNovaDataInicio}
                  autoFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {novaDataConclusao && (
            <div className="text-sm">
              <p className="text-muted-foreground">Nova data de conclusão calculada:</p>
              <p className="font-medium">{format(novaDataConclusao, "dd/MM/yyyy", { locale: ptBR })}</p>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleConfirm}
              disabled={!novaDataInicio || !novaDataConclusao}
              className="flex-1"
            >
              Confirmar Renovação
            </Button>
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
